import { forwardRef, useImperativeHandle, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography } from '@mui/material'
import { useMutation } from '@apollo/client'

import { Button, Stack, Text, NotificationDialog } from '../../../components'
import { mutations } from '../../../graphql'

export const OTHER_PAYMENT_METHODS = [
  'estate-gifts',
  'crypto-currency',
  'flow-through-shares',
  'insurance-gifts',
  'shares-of-private-corporations',
]
export async function getPaymentValues({ values }) {
  return {
    paymentMethodType: values.paymentMethodType,
    selectedGivingOptions: [values.paymentMethodType],
  }
}
export const getPaymentMethodDisplayName = (paymentMethod) => {
  const displayNames = {
    'estate-gifts': 'Estate Gifts',
    'crypto-currency': 'Cryptocurrency',
    'flow-through-shares': 'Flow-Through Shares',
    'insurance-gifts': 'Insurance Gifts',
    'shares-of-private-corporations': 'Private Corporation Shares',
  }
  return displayNames[paymentMethod] || paymentMethod
}

export function SuccessDialog({
  open,
  onClose,
  isLoggedIn = false,
  redirectPath = '/sign-up/signup',
  doneRedirectPath = '/funds/',
  fundId,
  selectedOption,
  userEmail,
  isDonatingToFund,
  donationTargetName,
  donationTargetId,
}) {
  const navigate = useNavigate()
  const [emailSent, setEmailSent] = useState(false)
  const [emailError, setEmailError] = useState(null)

  const [requestGivingOptionsInfo] = useMutation(mutations.user.requestGivingOptionsInfo, {
    onCompleted: (data) => {
      setEmailSent(true)
    },
    onError: (error) => {
      setEmailError(error.message)
    },
  })

  useEffect(() => {
    if (open && !emailSent && selectedOption) {
      requestGivingOptionsInfo({
        variables: {
          options: [selectedOption],
          email: userEmail,
          fundId,
          isDonatingToFund,
          donationTargetName,
          donationTargetId,
        },
      })
    }
  }, [open, emailSent, requestGivingOptionsInfo, selectedOption, userEmail, fundId])

  if (emailError) {
    return (
      <NotificationDialog
        open={open}
        onClose={onClose}
        type="error"
        title="Error Sending Request"
        message={`There was an issue sending your request: ${emailError}. Please try again or contact support.`}
        actions={[{ label: 'Close', onClick: onClose }]}
      />
    )
  }

  return (
    <NotificationDialog
      open={open}
      onClose={
        isLoggedIn
          ? () => {
              onClose()
              navigate(doneRedirectPath)
            }
          : () => {
              onClose()
              navigate(redirectPath)
            }
      }
      type="success"
      title="Thank you for your interest!"
      message={
        <>
          <Text.Body sx={{ mb: 3 }}>
            <strong>Thank you for expressing interest in planning a future gift!</strong>
          </Text.Body>
          <Text.Body sx={{ mb: 3 }}>
            Our team will connect with you shortly to discuss donating{' '}
            <strong>{getPaymentMethodDisplayName(selectedOption)}</strong>
          </Text.Body>
          <Text.Body sx={{ mb: 3 }}>
            Keep an eye on your inbox — and if you don&apos;t hear from us soon, be sure to check your spam or junk
            folder just in case.
          </Text.Body>
          {!isLoggedIn && (
            <>
              <Text.Body sx={{ mb: 2 }}>
                <strong>Make Giving Easier - Create Your Giving Fund Today</strong>
              </Text.Body>
              <Text.Body sx={{ mb: 1 }}>Add a password and set up your Giving Fund to:</Text.Body>
              <Text.Body component="div" sx={{ mb: 2 }}>
                <ul style={{ marginBottom: '1rem' }}>
                  <li>Instantly access a single tax receipt for all your donations, anytime.</li>
                  <li>Set up recurring gifts with ease.</li>
                  <li>Organize and manage all your donations in one convenient place.</li>
                </ul>
              </Text.Body>
            </>
          )}
        </>
      }
      actions={[
        {
          label: isLoggedIn ? 'Done' : 'Add Password',
          onClick: () => {
            onClose()
            navigate(isLoggedIn ? doneRedirectPath : redirectPath)
          },
        },
      ]}
    />
  )
}

export const PaymentUI = forwardRef(({ paymentType = 'this giving method' }, ref) => {
  useImperativeHandle(ref, () => ({}))

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Request Information for {paymentType}</Typography>
      <Text.Body>
        Our team will reach out to you with detailed information about donating through {paymentType}. This specialized
        giving method may offer unique tax advantages and impact opportunities.
      </Text.Body>
    </Stack>
  )
})
